import { useCallback, useEffect, useState } from "react";
import {
  clinicalApi,
  type CurrentUser,
  type InfectionReviewRecord,
} from "../../lib/api/clinical";
import { formatDateTime } from "./format";

const VERDICT_LABELS: Record<string, string> = {
  pending: "待复核",
  observing: "继续观察",
  confirmed: "确认CAUTI",
  excluded: "已排除",
  returned: "退回补录",
};

interface Props {
  user: CurrentUser;
}

export function ReviewPanel({ user }: Props) {
  const [rows, setRows] = useState<InfectionReviewRecord[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const canWrite = user.role === "admin" || user.role === "infection_control";

  const load = useCallback(async () => {
    const data = await clinicalApi.infectionReviews();
    setRows(data);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load().catch(() => {
      setMessage("加载复核队列失败");
    });
  }, [load]);

  async function decide(id: string, verdict: string) {
    setBusy(true);
    setMessage("");
    try {
      await clinicalApi.decideInfectionReview(id, verdict);
      await load();
      setMessage(`已标记：${VERDICT_LABELS[verdict] ?? verdict}`);
    } catch {
      setMessage("操作失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="view">
      <div className="section-head">
        <h2>院感病例复核</h2>
        {message ? <span role="status">{message}</span> : null}
      </div>
      {rows.length === 0 ? (
        <p>暂无待复核病例。高危患者在处置页「转院感复核」后进入此队列。</p>
      ) : (
        <table className="queue-table">
          <thead>
            <tr>
              <th>创建时间</th>
              <th>结论</th>
              <th>复核时间</th>
              {canWrite ? <th>判定</th> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((review) => (
              <tr key={review.id}>
                <td>{formatDateTime(review.created_at)}</td>
                <td>{VERDICT_LABELS[review.verdict] ?? review.verdict}</td>
                <td>{formatDateTime(review.reviewed_at)}</td>
                {canWrite ? (
                  <td className="row-actions">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void decide(review.id, "confirmed")}
                    >
                      确认
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void decide(review.id, "excluded")}
                    >
                      排除
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void decide(review.id, "observing")}
                    >
                      继续观察
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
